import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';
import { useAdaptiveExperienceOptional } from './AdaptiveExperienceContext';
import {
  buildAdaptiveChallenges,
  buildRelationshipConfidenceBand,
  buildCounterfactualScript,
  buildCoupleOperatingSystemScore,
  buildRelationshipIntelligenceModel,
  buildRelationshipQuickWins,
  buildRelationshipTwin,
  compileRepairPlan,
  recordCounterfactualOutcome,
  simulateCounterfactual,
  type RelationshipAdaptiveChallenge,
  type RelationshipConfidenceBand,
  type CounterfactualScript,
  type CounterfactualProjection,
  type CounterfactualStyle,
  type CoupleOperatingSystemScore,
  type RelationshipIntelligenceModel,
  type RelationshipQuickWin,
  type RelationshipTwinSnapshot,
  type RepairCompilerPlan,
} from '../lib/relationship-intelligence-engine';

export type RelationshipIntelligenceContextValue = {
  model: RelationshipIntelligenceModel;
  twin: RelationshipTwinSnapshot;
  repairPlan: RepairCompilerPlan;
  osScore: CoupleOperatingSystemScore;
  confidenceBand: RelationshipConfidenceBand;
  quickWins: RelationshipQuickWin[];
  adaptiveChallenges: RelationshipAdaptiveChallenge[];
  projectCounterfactual: (style: CounterfactualStyle) => CounterfactualProjection;
  scriptForStyle: (style: CounterfactualStyle) => CounterfactualScript;
  selectCounterfactualPath: (style: CounterfactualStyle) => void;
  refresh: () => void;
};

type PendingCounterfactualSelection = {
  style: CounterfactualStyle;
  selectedAt: number;
  predictedDelta: number;
  scoreAtSelection: number;
  sampleCountAtSelection: number;
};

const PENDING_COUNTERFACTUAL_SELECTION_KEY = 'tension_pending_counterfactual_selection_v1';

function readCheckinsForActiveRelationship(): Array<Record<string, unknown>> {
  try {
    const activeRelationshipId = localStorage.getItem('tension_active_relationship') || '';
    const raw = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    if (!Array.isArray(raw)) return [];
    if (!activeRelationshipId) return raw as Array<Record<string, unknown>>;
    return (raw as Array<Record<string, unknown>>).filter(
      (c) => String((c as Record<string, unknown>).relationshipId || '') === activeRelationshipId,
    );
  } catch {
    return [];
  }
}

const RelationshipIntelligenceContext = createContext<RelationshipIntelligenceContextValue | null>(null);

export function RelationshipIntelligenceProvider({ children }: { children: ReactNode }) {
  const adaptive = useAdaptiveExperienceOptional();
  const [revision, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision((v) => v + 1), []);

  useEffect(() => {
    const onCheckins = () => refresh();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'tension_checkins' || e.key === 'tension_active_relationship') refresh();
    };
    window.addEventListener(CHECKINS_UPDATED_EVENT, onCheckins);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHECKINS_UPDATED_EVENT, onCheckins);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => refresh(), 90_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const checkins = useMemo(() => {
    void revision;
    return readCheckinsForActiveRelationship();
  }, [revision]);

  const model = useMemo(() => {
    void revision;
    return buildRelationshipIntelligenceModel(checkins, adaptive?.profile ?? null);
  }, [adaptive?.profile, checkins, revision]);

  const twin = useMemo(() => buildRelationshipTwin(model, adaptive?.profile ?? null), [adaptive?.profile, model]);
  const repairPlan = useMemo(() => compileRepairPlan(model, twin, adaptive?.profile ?? null), [adaptive?.profile, model, twin]);
  const osScore = useMemo(() => buildCoupleOperatingSystemScore(checkins, model), [checkins, model]);
  const confidenceBand = useMemo(() => buildRelationshipConfidenceBand(model), [model]);
  const quickWins = useMemo(() => buildRelationshipQuickWins(model, twin), [model, twin]);
  const adaptiveChallenges = useMemo(
    () => buildAdaptiveChallenges(model, twin, osScore),
    [model, osScore, twin],
  );
  const projectCounterfactual = useCallback(
    (style: CounterfactualStyle) => simulateCounterfactual(model, style, adaptive?.profile ?? null),
    [adaptive?.profile, model],
  );
  const scriptForStyle = useCallback((style: CounterfactualStyle) => buildCounterfactualScript(style, model, twin), [model, twin]);

  const selectCounterfactualPath = useCallback(
    (style: CounterfactualStyle) => {
      const projection = simulateCounterfactual(model, style, adaptive?.profile ?? null);
      const payload: PendingCounterfactualSelection = {
        style,
        selectedAt: Date.now(),
        predictedDelta: projection.delta24h,
        scoreAtSelection: model.scoreNow,
        sampleCountAtSelection: model.sampleCount,
      };
      try {
        localStorage.setItem(PENDING_COUNTERFACTUAL_SELECTION_KEY, JSON.stringify(payload));
      } catch {
        // ignore storage errors
      }
    },
    [adaptive?.profile, model],
  );

  useEffect(() => {
    let pending: PendingCounterfactualSelection | null = null;
    try {
      pending = JSON.parse(localStorage.getItem(PENDING_COUNTERFACTUAL_SELECTION_KEY) || 'null') as PendingCounterfactualSelection | null;
    } catch {
      pending = null;
    }
    if (!pending) return;
    if (model.sampleCount <= pending.sampleCountAtSelection) return;
    if (model.scoreNow === pending.scoreAtSelection) return;

    const actualDelta = Number((pending.scoreAtSelection - model.scoreNow).toFixed(1));
    recordCounterfactualOutcome({
      at: Date.now(),
      style: pending.style,
      predictedDelta: pending.predictedDelta,
      actualDelta,
      scoreBefore: pending.scoreAtSelection,
      scoreAfter: model.scoreNow,
      sampleCountBefore: pending.sampleCountAtSelection,
      sampleCountAfter: model.sampleCount,
    });
    try {
      localStorage.removeItem(PENDING_COUNTERFACTUAL_SELECTION_KEY);
    } catch {
      // ignore storage errors
    }
  }, [model.sampleCount, model.scoreNow]);

  return (
    <RelationshipIntelligenceContext.Provider
      value={{
        model,
        twin,
        repairPlan,
        osScore,
        confidenceBand,
        quickWins,
        adaptiveChallenges,
        projectCounterfactual,
        scriptForStyle,
        selectCounterfactualPath,
        refresh,
      }}
    >
      {children}
    </RelationshipIntelligenceContext.Provider>
  );
}

export function useRelationshipIntelligence() {
  const ctx = useContext(RelationshipIntelligenceContext);
  if (!ctx) {
    throw new Error('useRelationshipIntelligence must be used within RelationshipIntelligenceProvider');
  }
  return ctx;
}

export function useRelationshipIntelligenceOptional() {
  return useContext(RelationshipIntelligenceContext);
}
